import { useEffect, useState } from 'react';

export interface GlobusHealthState {
  /** True once the health probe has observed the Hub's `globus` indicator as
   *  down (server/src/health/health.service.ts). Drives the amber
   *  DegradedBanner and the mutation gate in lib/api.ts. */
  degraded: boolean;
  /** Timestamp of the most recent probe that returned ANY result (up or
   *  down) — the "last checked" display. Null until the first probe resolves. */
  lastChecked: Date | null;
}

const POLL_MS = 30_000; // integration contract: retry every 30s
const HEALTH_PATH = '/api/v1/health';
const PROBE_TIMEOUT_MS = 8_000;

let state: GlobusHealthState = { degraded: false, lastChecked: null };
const listeners = new Set<(next: GlobusHealthState) => void>();
let started = false;
let inflight = false;

function setState(next: GlobusHealthState) {
  state = next;
  listeners.forEach((l) => l(state));
}

/**
 * GET /api/v1/health returns 503 (with the report body intact) when any
 * indicator is down (health.controller.ts throws
 * ServiceUnavailableException(report)), so the `globus` field is read from
 * the parsed body regardless of res.ok — never from res.status alone.
 */
async function probe(): Promise<void> {
  if (inflight) return;
  inflight = true;
  try {
    const base = import.meta.env.VITE_API_BASE_URL ?? '';
    const res = await fetch(`${base}${HEALTH_PATH}`, { signal: AbortSignal.timeout(PROBE_TIMEOUT_MS) });
    const body: { info?: { globus?: { status?: string } } } | null = await res.json().catch(() => null);
    const globusUp = body?.info?.globus?.status === 'up';
    setState({ degraded: !globusUp, lastChecked: new Date() });
  } catch {
    // A network failure reaching our OWN BFF is the client-offline case,
    // already owned by useOnline()/OfflineBanner — don't flip the
    // upstream-degraded flag on it, and don't update lastChecked (we didn't
    // actually learn anything about Globus).
  } finally {
    inflight = false;
  }
}

/** Starts the shared poll loop exactly once, regardless of how many
 *  consumers subscribe (the banner via the hook, api.ts via the getter). */
function ensureStarted() {
  if (started) return;
  started = true;
  void probe();
  setInterval(() => void probe(), POLL_MS);
  addEventListener('focus', () => void probe());
  addEventListener('online', () => void probe());
}

/** Synchronous read for non-React callers (lib/api.ts's mutation gate). */
export function isGlobusDegraded(): boolean {
  ensureStarted();
  return state.degraded;
}

/** React hook for the DegradedBanner. */
export function useGlobusHealth(): GlobusHealthState {
  ensureStarted();
  const [s, setS] = useState(state);
  useEffect(() => {
    const listener = (next: GlobusHealthState) => setS(next);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);
  return s;
}

/** Test-only reset — the module-level singleton otherwise leaks state and
 *  timers across test files. */
export function __resetGlobusHealthForTests(): void {
  state = { degraded: false, lastChecked: null };
  listeners.clear();
  started = false;
  inflight = false;
}
